((scope) => {
  "use strict";

  // Browser side of the SI-MP-00 commerce contract.
  //
  // The browser is untrusted and holds no commercial authority. It never derives a
  // Magic Preview Product Key, an internal Shopify SKU, a supplier SKU or a price,
  // and it never selects a pre-existing Shopify product or variant. It states which
  // Magic Preview configuration the customer accepted, and uses only the numeric
  // Shopify ProductVariant ID that SI-MP-00 returns.

  const GENERATED_ARTWORK_GEOMETRY_VERSION = 1;
  // Mirrors integrations/recraft-magic-preview/src/placement.js.
  const DEFAULT_PLACEMENT = Object.freeze({ offset_x: 0, offset_y: 0, scale: 0.9 });
  const SHA256_PATTERN = /^sha256:[a-f0-9]{64}$/i;
  const PRODUCT_KEY_PATTERN = /^mp_[a-f0-9]{64}$/i;
  const MODEL_DEMOGRAPHICS = new Map([
    ["adult-hoodie", "adult"],
    ["adult-sweatshirt", "adult"],
    ["adult-tee", "adult"],
    ["kids-hoodie", "child"],
    ["kids-sweatshirt", "child"],
    ["kids-tee", "child"],
    ["tote-bag", "adult"],
    ["beanie", "adult"],
    ["cap", "adult"],
  ]);

  // The exact wire contract. Both lists are closed: an unexpected key in either
  // direction is a contract violation, not something to tolerate.
  // The composition contract. One Magic Preview line is one composition of one
  // or two placements, front before back, and si-mp-key-v3 is computed from it
  // by Cloud Run. Mirrors integrations/recraft-magic-preview/src/product-creation-contract-v3.js.
  const KEY_ALGORITHM_V3_VERSION = "si-mp-key-v3";
  const PLACEMENT_SIDES = Object.freeze(["front", "back"]);
  const PLACEMENT_SIDE_ORDER = new Map(PLACEMENT_SIDES.map((side, index) => [side, index]));
  const TRANSFORM_PRECISION = 3;
  const DESIGN_ID_PATTERN = /^[a-z0-9][a-z0-9_-]{0,62}$/;
  const V3_REQUEST_FIELDS = Object.freeze([
    "product_id",
    "colour_id",
    "selected_option_key",
    "session_id",
    "iteration",
    "approved_preview_url",
    "approved_preview_url_back",
    "key_algorithm_version",
    "placements",
  ]);
  const V3_PLACEMENT_FIELDS = Object.freeze([
    "side",
    "design_id",
    "design_hash",
    "artwork_hash",
    "artwork_url",
    "artwork_geometry_version",
    "placement_geometry_version",
    "offset_x",
    "offset_y",
    "scale",
  ]);

  const REQUEST_FIELDS = Object.freeze([
    "product_id",
    "colour_id",
    "selected_option_key",
    "design_id",
    "design_hash",
    "artwork_hash",
    "artwork_geometry_version",
    "session_id",
    "iteration",
    "approved_preview_url",
  ]);
  const RESPONSE_FIELDS = Object.freeze([
    "success",
    "product_id",
    "colour_id",
    "selected_option_key",
    "design_id",
    "design_hash",
    "artwork_hash",
    "magic_preview_product_key",
    "shopify_variant_id",
  ]);
  // SI-MP-00 also reports the storefront handle of the product it provisioned.
  // Nothing here trusts it, so it is accepted and ignored rather than treated as
  // a contract violation. Every field the cart actually relies on is still
  // required and still checked against the request below.
  const RESPONSE_OPTIONAL_FIELDS = Object.freeze(["shopify_product_handle"]);
  const REQUEST_FIELD_SET = new Set(REQUEST_FIELDS);
  const V3_REQUEST_FIELD_SET = new Set(V3_REQUEST_FIELDS);
  const V3_PLACEMENT_FIELD_SET = new Set(V3_PLACEMENT_FIELDS);
  const RESPONSE_FIELD_SET = new Set(RESPONSE_FIELDS);
  const RESPONSE_ACCEPTED_FIELD_SET = new Set([...RESPONSE_FIELDS, ...RESPONSE_OPTIONAL_FIELDS]);

  const plainObject = (value) => Boolean(value)
    && typeof value === "object"
    && !Array.isArray(value)
    && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);

  const invalidSiMp00Response = (value) => {
    const receivedFields = plainObject(value) ? Object.keys(value) : [];
    const error = new Error("invalid_si_mp_00_response");
    // This is a preparation failure, not a cart failure. Saying so keeps the
    // customer from being told the bag rejected an item that never reached it.
    error.customerMessage = "We couldn't prepare this personalised item. Please try again.";
    error.diagnostic = Object.freeze({
      response_type: value === null ? "null" : Array.isArray(value) ? "array" : typeof value,
      success_is_true: plainObject(value) && value.success === true,
      missing_fields: RESPONSE_FIELDS.filter((field) => !receivedFields.includes(field)),
      unexpected_fields: receivedFields.filter((field) => !RESPONSE_ACCEPTED_FIELD_SET.has(field)),
    });
    return error;
  };

  const text = (value, field, { optional = false, maxLength = 256 } = {}) => {
    if (optional && (value === undefined || value === null || value === "")) return null;
    if (
      typeof value !== "string"
      || !value
      || value !== value.trim()
      || value.length > maxLength
      || /[\u0000-\u001f\u007f]/.test(value)
    ) {
      throw new Error(`invalid_${field}`);
    }
    return value;
  };

  const nullableHash = (value, field) => {
    const normalized = text(value, field, { optional: true, maxLength: 71 });
    if (normalized !== null && !SHA256_PATTERN.test(normalized)) throw new Error(`invalid_${field}`);
    return normalized === null ? null : normalized.toLowerCase();
  };

  const requiredHash = (value, field) => {
    const normalized = nullableHash(value, field);
    if (normalized === null) throw new Error(`missing_${field}`);
    return normalized;
  };

  const artworkGeometryVersion = (value) => {
    if (value !== 0 && value !== 1) throw new Error("unknown_artwork_geometry_version");
    return value;
  };

  const artworkHashForGeometry = (value, geometryVersion) => {
    const normalized = nullableHash(value, "artwork_hash");
    if (geometryVersion === 1 && normalized === null) throw new Error("missing_artwork_hash");
    return normalized;
  };

  const artworkUrl = (value) => {
    const raw = text(value, "artwork_url", { maxLength: 2048 });
    let url;
    try { url = new URL(raw); } catch { throw new Error("invalid_artwork_url"); }
    const loopback = url.protocol === "http:"
      && ["127.0.0.1", "localhost", "[::1]"].includes(url.hostname);
    if ((url.protocol !== "https:" && !loopback) || url.username || url.password) {
      throw new Error("invalid_artwork_url");
    }
    return url.toString();
  };

  const placementNumber = (value, field, min, max) => {
    const number = Number(value);
    if (!Number.isFinite(number)) throw new Error(`invalid_${field}`);
    return Math.min(max, Math.max(min, Object.is(number, -0) ? 0 : number));
  };

  const normalizePlacement = (value = DEFAULT_PLACEMENT) => Object.freeze({
    offset_x: placementNumber(value?.offset_x ?? DEFAULT_PLACEMENT.offset_x, "offset_x", -1, 1),
    offset_y: placementNumber(value?.offset_y ?? DEFAULT_PLACEMENT.offset_y, "offset_y", -1, 1),
    scale: placementNumber(value?.scale ?? DEFAULT_PLACEMENT.scale, "scale", 0.55, 1),
  });

  function placementForGeometryTransition({
    previous_geometry_version,
    next_geometry_version,
    previous_placement,
  }) {
    const nextVersion = artworkGeometryVersion(next_geometry_version);
    if (previous_geometry_version === undefined || previous_geometry_version === null) {
      return normalizePlacement(DEFAULT_PLACEMENT);
    }
    const previousVersion = artworkGeometryVersion(previous_geometry_version);
    return previousVersion === nextVersion
      ? normalizePlacement(previous_placement)
      : normalizePlacement(DEFAULT_PLACEMENT);
  }

  function normalizeGeneratedArtworkIdentity(input) {
    if (!plainObject(input)) throw new Error("invalid_generated_artwork_identity");
    const geometryVersion = artworkGeometryVersion(input.artwork_geometry_version);
    return Object.freeze({
      artwork_url: artworkUrl(input.artwork_url),
      artwork_hash: artworkHashForGeometry(input.artwork_hash, geometryVersion),
      artwork_geometry_version: geometryVersion,
      design_id: text(input.design_id, "design_id"),
      design_hash: requiredHash(input.design_hash, "design_hash"),
    });
  }

  const iteration = (value) => {
    if (!Number.isSafeInteger(value) || value < 1 || value > 1_000_000) {
      throw new Error("invalid_iteration");
    }
    return value;
  };

  const approvedPreviewUrl = (value, productId, colourId, { allowBackSide = false } = {}) => {
    const raw = text(value, "approved_preview_url", { maxLength: 4096 });
    let url;
    try {
      url = new URL(raw);
    } catch {
      throw new Error("invalid_approved_preview_url");
    }
    const loopback = url.protocol === "http:"
      && ["127.0.0.1", "localhost", "[::1]"].includes(url.hostname);
    if (
      (url.protocol !== "https:" && !loopback)
      || url.username
      || url.password
      || url.pathname !== "/api/magic-preview/approved-preview"
    ) {
      throw new Error("invalid_approved_preview_url");
    }
    const fields = ["product_id", "colour_id", "artwork", "offset_x", "offset_y", "scale"];
    // Only a back placement states a side, so every front preview URL keeps the
    // exact shape SI-MP-00 and the cart have always been given.
    const optionalFields = allowBackSide
      ? ["placement_geometry_version", "side"]
      : ["placement_geometry_version"];
    const keys = [...url.searchParams.keys()];
    if (
      fields.some((field) => url.searchParams.getAll(field).length !== 1)
      || optionalFields.some((field) => url.searchParams.getAll(field).length > 1)
      || keys.some((field) => !fields.includes(field) && !optionalFields.includes(field))
    ) {
      throw new Error("invalid_approved_preview_url");
    }
    if (url.searchParams.get("product_id") !== productId) {
      throw new Error("mismatched_approved_preview_product_id");
    }
    if (url.searchParams.get("colour_id") !== colourId) {
      throw new Error("mismatched_approved_preview_colour_id");
    }
    if (url.searchParams.has("side") && url.searchParams.get("side") !== "back") {
      throw new Error("invalid_approved_preview_side");
    }
    return url.toString();
  };

  // design_hash is a component of the Magic Preview Product Key, so it must
  // identify the *printable artwork* and nothing else.
  //
  // Deliberately excluded:
  //   placement (offset_x / offset_y / scale) — a continuous per-order print
  //     instruction. Including it would mint a distinct Shopify product for every
  //     pixel the customer drags the artwork, without bound.
  //   school_name — not a browser → SI-MP-00 input and explicitly not part of
  //     the product key. Any authoritative structured value remains optional
  //     presentation metadata outside this provisioning identity boundary.
  //
  // Placement travels to fulfilment as cart line properties instead.
  function normalizeCatalogOptions(value) {
    if (!Array.isArray(value)) return [];
    const seen = new Set();
    let dimensions = null;
    return value.map((option) => {
      if (!plainObject(option)) throw new Error("invalid_catalog_option");
      const optionKey = text(option.option_key, "option_key");
      if (seen.has(optionKey)) throw new Error("duplicate_option_key");
      seen.add(optionKey);
      if (!Array.isArray(option.shopify_option_values) || !option.shopify_option_values.length) {
        throw new Error("missing_option_values");
      }
      if (option.shopify_option_values.length > 3) throw new Error("too_many_option_dimensions");
      const optionValues = option.shopify_option_values.map((entry) => {
        if (!plainObject(entry)) throw new Error("invalid_option_value");
        return Object.freeze({
          option_name: text(entry.option_name, "option_name", { maxLength: 80 }),
          value: text(entry.value, "option_value", { maxLength: 160 }),
          // Optional presentation metadata may come from the authoritative
          // catalogue. It never replaces the canonical Shopify option value.
          display_value: text(entry.display_value, "option_display_value", {
            optional: true,
            maxLength: 160,
          }),
        });
      });
      // Every option in a set must describe the same dimensions in the same order,
      // otherwise "one selection resolves to one option_key" cannot hold.
      const signature = optionValues.map((entry) => entry.option_name).join("\u001f");
      if (dimensions === null) dimensions = signature;
      else if (dimensions !== signature) throw new Error("inconsistent_option_dimensions");
      return Object.freeze({
        option_key: optionKey,
        option_label: text(option.option_label, "option_label", { maxLength: 200 }),
        shopify_option_values: Object.freeze(optionValues),
        available: option.available !== false,
      });
    });
  }

  // Names of the dimensions a set of options varies over, in contract order.
  function optionDimensions(options) {
    if (!options.length) return [];
    return options[0].shopify_option_values.map((entry) => entry.option_name);
  }

  // The customer-facing label. A single dimension shows the plain label the
  // contract supplied ("M"); multiple dimensions are joined without exposing any
  // technical identifier.
  function optionDisplayLabel(option) {
    if (option.shopify_option_values.length === 1) return option.option_label;
    return option.shopify_option_values.map((entry) => entry.value).join(" / ");
  }

  // Resolves a chosen value per dimension to exactly one option_key. Identity is
  // never taken from array position.
  function resolveOptionKey(options, selection) {
    if (!plainObject(selection)) throw new Error("invalid_option_selection");
    const dimensions = optionDimensions(options);
    if (!dimensions.length) throw new Error("no_options_available");
    if (Object.keys(selection).some((name) => !dimensions.includes(name))) {
      throw new Error("unknown_option_dimension");
    }
    if (dimensions.some((name) => typeof selection[name] !== "string" || !selection[name])) {
      throw new Error("incomplete_option_selection");
    }
    const matches = options.filter((option) => option.shopify_option_values
      .every((entry) => selection[entry.option_name] === entry.value));
    if (matches.length !== 1) throw new Error("option_selection_not_unique");
    return matches[0].option_key;
  }

  // Distinct values for one dimension, in contract order, de-duplicated.
  function optionValuesForDimension(options, dimension) {
    return optionChoicesForDimension(options, dimension).map((choice) => choice.value);
  }

  // Keeps presentation and identity separate. `value` is always the canonical
  // Shopify option value used to resolve `option_key`; `label` may use an
  // authoritative display value, or (for a one-dimensional product) the
  // catalogue's authoritative option_label. No browser-owned size conversion is
  // invented when neither mapping exists.
  function optionChoicesForDimension(options, dimension) {
    const dimensions = optionDimensions(options);
    if (!dimensions.includes(dimension)) throw new Error("unknown_option_dimension");
    const choices = [];
    for (const option of options) {
      const entry = option.shopify_option_values.find((item) => item.option_name === dimension);
      if (!entry || choices.some((choice) => choice.value === entry.value)) continue;
      const matchingEntries = options
        .map((candidate) => candidate.shopify_option_values
          .find((item) => item.option_name === dimension && item.value === entry.value))
        .filter(Boolean);
      const explicitLabels = [...new Set(matchingEntries
        .map((item) => item.display_value)
        .filter(Boolean))];
      if (explicitLabels.length > 1) throw new Error("inconsistent_option_display_values");

      let label = explicitLabels[0] || null;
      if (!label && dimensions.length === 1) {
        const optionLabels = [...new Set(options
          .filter((candidate) => candidate.shopify_option_values[0].value === entry.value)
          .map((candidate) => candidate.option_label))];
        if (optionLabels.length > 1) throw new Error("inconsistent_option_display_values");
        label = optionLabels[0] || null;
      }
      choices.push(Object.freeze({ value: entry.value, label: label || entry.value }));
    }
    return Object.freeze(choices);
  }

  function modelDemographicForProduct(productId) {
    const canonicalProductId = text(productId, "product_id");
    const demographic = MODEL_DEMOGRAPHICS.get(canonicalProductId);
    if (!demographic) throw new Error("unknown_model_product_family");
    return demographic;
  }

  function transform(value, field, min, max) {
    const number = Number(value);
    if (!Number.isFinite(number) || number < min || number > max) throw new Error(`invalid_${field}`);
    // The contract carries three decimals, and the composition identity is
    // computed from exactly those, so the browser sends what will be hashed.
    const rounded = Number(number.toFixed(TRANSFORM_PRECISION));
    return Object.is(rounded, -0) ? 0 : rounded;
  }

  // Production resolves the artwork by content address, so a placement URL must
  // carry the hash it claims. SI-MP-00 refuses a composition where it does not.
  function placementArtworkUrl(value, hash) {
    const normalized = artworkUrl(value);
    if (!new URL(normalized).pathname.includes(hash.slice("sha256:".length))) {
      throw new Error("artwork_url_hash_mismatch");
    }
    return normalized;
  }

  function normalizeCompositionPlacement(value) {
    if (
      !plainObject(value)
      || V3_PLACEMENT_FIELDS.some((field) => !Object.hasOwn(value, field))
      || Object.keys(value).some((field) => !V3_PLACEMENT_FIELD_SET.has(field))
    ) {
      throw new Error("invalid_placement");
    }
    const side = text(value.side, "side", { maxLength: 20 });
    if (!PLACEMENT_SIDE_ORDER.has(side)) throw new Error("invalid_side");
    const designId = text(value.design_id, "design_id", { maxLength: 63 });
    if (!DESIGN_ID_PATTERN.test(designId)) throw new Error("invalid_design_id");
    // A composition is only ever authored under the current artwork and supplier
    // placement geometry; a legacy placement stays on the v2 path unchanged.
    if (value.artwork_geometry_version !== GENERATED_ARTWORK_GEOMETRY_VERSION) {
      throw new Error("unknown_artwork_geometry_version");
    }
    if (value.placement_geometry_version !== 2) {
      throw new Error("unknown_placement_geometry_version");
    }
    const artworkHash = requiredHash(value.artwork_hash, "artwork_hash");
    return Object.freeze({
      side,
      design_id: designId,
      design_hash: requiredHash(value.design_hash, "design_hash"),
      artwork_hash: artworkHash,
      artwork_url: placementArtworkUrl(value.artwork_url, artworkHash),
      artwork_geometry_version: GENERATED_ARTWORK_GEOMETRY_VERSION,
      placement_geometry_version: 2,
      offset_x: transform(value.offset_x, "offset_x", -1, 1),
      offset_y: transform(value.offset_y, "offset_y", -1, 1),
      scale: transform(value.scale, "scale", 0.55, 1),
    });
  }

  function normalizeCompositionPlacements(value) {
    if (!Array.isArray(value) || value.length < 1 || value.length > PLACEMENT_SIDES.length) {
      throw new Error("invalid_placements");
    }
    const placements = value.map(normalizeCompositionPlacement);
    const sides = placements.map((placement) => placement.side);
    if (new Set(sides).size !== sides.length) throw new Error("duplicate_placement_side");
    // Front before back: the order the composition key is computed in.
    return Object.freeze([...placements].sort(
      (left, right) => PLACEMENT_SIDE_ORDER.get(left.side) - PLACEMENT_SIDE_ORDER.get(right.side),
    ));
  }

  // SI-MP-00 stores the approved preview as the Shopify product image, so it
  // must depict the leading placement: the front whenever one is printed, and
  // otherwise the back.
  function compositionPreviewUrl(value, productId, colourId, placements) {
    const url = new URL(approvedPreviewUrl(value, productId, colourId, { allowBackSide: true }));
    const leading = placements[0];
    const side = url.searchParams.get("side");
    if (side !== (leading.side === "back" ? "back" : null)) {
      throw new Error("mismatched_approved_preview_side");
    }
    if (url.searchParams.get("artwork") !== leading.artwork_url) {
      throw new Error("mismatched_approved_preview_artwork");
    }
    return url.toString();
  }

  // The Shopify product carries one image per printed side, so a composition of
  // two placements also states the trailing side's approved preview. It is the
  // same signed identity as the leading one, for the other side, and a
  // single-placement composition states null rather than repeating an image.
  //
  // SI-MP-00 turns these into the product's media: the leading preview is the
  // first file and this one is the second. Until its productSet mapping attaches
  // it, a two-sided product page shows the front only.
  function compositionTrailingPreviewUrl(value, productId, colourId, placements) {
    const trailing = placements[1] || null;
    if (!trailing) {
      if (value !== null && value !== undefined) throw new Error("unexpected_approved_preview_url_back");
      return null;
    }
    const url = new URL(approvedPreviewUrl(value, productId, colourId, { allowBackSide: true }));
    if (url.searchParams.get("side") !== (trailing.side === "back" ? "back" : null)) {
      throw new Error("mismatched_approved_preview_back_side");
    }
    if (url.searchParams.get("artwork") !== trailing.artwork_url) {
      throw new Error("mismatched_approved_preview_back_artwork");
    }
    return url.toString();
  }

  // The exact wire contract for a front/back composition. SI-MP-00 recomputes
  // si-mp-key-v3 server-side from the production authority; nothing here is
  // commercial authority, and the browser never derives the key itself.
  function buildSiMp00PayloadV3(input) {
    if (
      !plainObject(input)
      || V3_REQUEST_FIELDS.some((field) => !Object.hasOwn(input, field))
      || Object.keys(input).some((key) => !V3_REQUEST_FIELD_SET.has(key))
    ) {
      throw new Error("invalid_si_mp_00_payload");
    }
    if (input.key_algorithm_version !== KEY_ALGORITHM_V3_VERSION) {
      throw new Error("unsupported_key_algorithm_version");
    }
    const productId = text(input.product_id, "product_id");
    const colourId = text(input.colour_id, "colour_id");
    const placements = normalizeCompositionPlacements(input.placements);
    return Object.freeze({
      product_id: productId,
      colour_id: colourId,
      selected_option_key: text(input.selected_option_key, "selected_option_key"),
      session_id: text(input.session_id, "session_id"),
      iteration: iteration(input.iteration),
      approved_preview_url: compositionPreviewUrl(input.approved_preview_url, productId, colourId, placements),
      approved_preview_url_back: compositionTrailingPreviewUrl(
        input.approved_preview_url_back,
        productId,
        colourId,
        placements,
      ),
      key_algorithm_version: KEY_ALGORITHM_V3_VERSION,
      placements,
    });
  }

  // Shopify identity, checked identically for both contract versions.
  function trustedProvisioning(value) {
    const productKey = text(value.magic_preview_product_key, "magic_preview_product_key");
    if (!PRODUCT_KEY_PATTERN.test(productKey)) throw new Error("invalid_magic_preview_product_key");
    const variantId = value.shopify_variant_id;
    if (typeof variantId !== "string") throw new Error("invalid_shopify_variant_id");
    if (!/^[1-9]\d{0,15}$/.test(variantId)) throw new Error("invalid_shopify_variant_id");
    const numericVariantId = Number(variantId);
    if (!Number.isSafeInteger(numericVariantId) || numericVariantId <= 0) {
      throw new Error("invalid_shopify_variant_id");
    }
    return Object.freeze({
      shopify_variant_id: variantId,
      magic_preview_product_key: productKey.toLowerCase(),
    });
  }

  // SI-MP-00 answers a composition with the same nine fields as v2. The design
  // identity it echoes is the leading placement's, which is exactly what its
  // Airtable mapping and the Shopify product metafields record.
  function validateSiMp00ResponseV3(value, request) {
    if (!plainObject(value) || value.success !== true) throw invalidSiMp00Response(value);
    const keys = Object.keys(value);
    if (
      RESPONSE_FIELDS.some((field) => !keys.includes(field))
      || keys.some((key) => !RESPONSE_ACCEPTED_FIELD_SET.has(key))
    ) {
      throw invalidSiMp00Response(value);
    }
    const expected = buildSiMp00PayloadV3(request);
    const leading = expected.placements[0];
    const checked = {
      product_id: text(value.product_id, "response_product_id"),
      colour_id: text(value.colour_id, "response_colour_id"),
      selected_option_key: text(value.selected_option_key, "response_selected_option_key"),
      design_id: text(value.design_id, "response_design_id"),
      design_hash: requiredHash(value.design_hash, "response_design_hash"),
      artwork_hash: requiredHash(value.artwork_hash, "response_artwork_hash"),
    };
    const against = {
      product_id: expected.product_id,
      colour_id: expected.colour_id,
      selected_option_key: expected.selected_option_key,
      design_id: leading.design_id,
      design_hash: leading.design_hash,
      artwork_hash: leading.artwork_hash,
    };
    for (const field of Object.keys(checked)) {
      if (checked[field] !== against[field]) throw new Error(`mismatched_${field}`);
    }
    return trustedProvisioning(value);
  }

  // What the cart line says about the composition. Production reads the
  // authority SI-MP-00 stored, never these values, so they are a faithful
  // record of the accepted sides and nothing more.
  function buildCompositionCartProperties(placements) {
    const properties = {
      "_magic_preview_key_algorithm_version": KEY_ALGORITHM_V3_VERSION,
      "_magic_preview_print_sides": placements.map((placement) => placement.side).join(","),
    };
    for (const placement of placements) {
      const prefix = `_magic_preview_${placement.side}_`;
      properties[`${prefix}design_id`] = placement.design_id;
      properties[`${prefix}design_hash`] = placement.design_hash;
      properties[`${prefix}artwork_hash`] = placement.artwork_hash;
      properties[`${prefix}artwork_url`] = placement.artwork_url;
      properties[`${prefix}artwork_geometry_version`] = String(placement.artwork_geometry_version);
      properties[`${prefix}placement_geometry_version`] = String(placement.placement_geometry_version);
      properties[`${prefix}offset_x`] = placement.offset_x.toFixed(TRANSFORM_PRECISION);
      properties[`${prefix}offset_y`] = placement.offset_y.toFixed(TRANSFORM_PRECISION);
      properties[`${prefix}scale`] = placement.scale.toFixed(TRANSFORM_PRECISION);
    }
    return Object.freeze(properties);
  }

  function buildSiMp00Payload(input) {
    if (!plainObject(input) || Object.keys(input).some((key) => !REQUEST_FIELD_SET.has(key))) {
      throw new Error("invalid_si_mp_00_payload");
    }
    const productId = text(input.product_id, "product_id");
    const colourId = text(input.colour_id, "colour_id");
    const geometryVersion = artworkGeometryVersion(input.artwork_geometry_version);
    const payload = {
      product_id: productId,
      colour_id: colourId,
      selected_option_key: text(input.selected_option_key, "selected_option_key"),
      design_id: text(input.design_id, "design_id"),
      design_hash: requiredHash(input.design_hash, "design_hash"),
      artwork_hash: artworkHashForGeometry(input.artwork_hash, geometryVersion),
      artwork_geometry_version: geometryVersion,
      session_id: text(input.session_id, "session_id"),
      iteration: iteration(input.iteration),
      approved_preview_url: approvedPreviewUrl(input.approved_preview_url, productId, colourId),
    };
    return Object.freeze(payload);
  }

  // Nothing reaches the cart unless SI-MP-00 echoed back the exact configuration
  // that was requested. A stale or mismatched response is rejected outright rather
  // than being reconciled.
  function validateSiMp00Response(value, request) {
    if (!plainObject(value) || value.success !== true) throw invalidSiMp00Response(value);
    const keys = Object.keys(value);
    // Every trusted field must be present, and nothing outside the known contract
    // may appear. A documented informational extra is allowed through; an unknown
    // key is still refused, so a rogue field can never be mistaken for a trusted one.
    if (RESPONSE_FIELDS.some((field) => !keys.includes(field))
      || keys.some((key) => !RESPONSE_ACCEPTED_FIELD_SET.has(key))) {
      throw invalidSiMp00Response(value);
    }
    const expected = buildSiMp00Payload(request);
    const checked = {
      product_id: text(value.product_id, "response_product_id"),
      colour_id: text(value.colour_id, "response_colour_id"),
      selected_option_key: text(value.selected_option_key, "response_selected_option_key"),
      design_id: text(value.design_id, "response_design_id"),
      design_hash: requiredHash(value.design_hash, "response_design_hash"),
      artwork_hash: nullableHash(value.artwork_hash, "response_artwork_hash"),
    };
    for (const field of Object.keys(checked)) {
      if (checked[field] !== expected[field]) throw new Error(`mismatched_${field}`);
    }
    const productKey = text(value.magic_preview_product_key, "magic_preview_product_key");
    if (!PRODUCT_KEY_PATTERN.test(productKey)) throw new Error("invalid_magic_preview_product_key");

    // The browser response contract represents Shopify ProductVariant identity as
    // a digit-string. A number, GID, padded string or float is a contract violation.
    const variantId = value.shopify_variant_id;
    if (typeof variantId !== "string") throw new Error("invalid_shopify_variant_id");
    if (!/^[1-9]\d{0,15}$/.test(variantId)) throw new Error("invalid_shopify_variant_id");
    const numericVariantId = Number(variantId);
    if (!Number.isSafeInteger(numericVariantId) || numericVariantId <= 0) {
      throw new Error("invalid_shopify_variant_id");
    }
    return Object.freeze({
      // Preserve the wire identity as the authoritative digit-string. The
      // storefront converts it only at the `/cart/add.js` boundary.
      shopify_variant_id: variantId,
      magic_preview_product_key: productKey.toLowerCase(),
    });
  }

  scope.SpiritInkMagicPreviewCommerce = Object.freeze({
    GENERATED_ARTWORK_GEOMETRY_VERSION,
    KEY_ALGORITHM_V3_VERSION,
    PLACEMENT_SIDES,
    REQUEST_FIELDS,
    RESPONSE_FIELDS,
    V3_PLACEMENT_FIELDS,
    V3_REQUEST_FIELDS,
    buildCompositionCartProperties,
    buildSiMp00Payload,
    buildSiMp00PayloadV3,
    normalizeGeneratedArtworkIdentity,
    normalizeCatalogOptions,
    modelDemographicForProduct,
    optionDimensions,
    optionChoicesForDimension,
    optionDisplayLabel,
    optionValuesForDimension,
    placementForGeometryTransition,
    resolveOptionKey,
    validateSiMp00Response,
    validateSiMp00ResponseV3,
  });
})(typeof window === "undefined" ? globalThis : window);
